import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, FileText, User, Database, Clock, Download, Trash2 } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            Volver
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <span className="font-semibold text-gray-900">Grupo Ibérica</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Política de Privacidad</h1>
            <p className="text-gray-500">Última actualización: 22 de enero de 2026</p>
            <p className="text-gray-500">Versión: 1.0</p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {/* 1. Responsable */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">1. Responsable del Tratamiento</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p><strong>Identidad:</strong> [Nombre de la empresa]</p>
                <p><strong>CIF:</strong> [CIF de la empresa]</p>
                <p><strong>Dirección:</strong> [Dirección fiscal]</p>
                <p><strong>Email:</strong> info@multaflow.com</p>
                <p><strong>Teléfono:</strong> [Teléfono de contacto]</p>
                <p><strong>Delegado de Protección de Datos (DPO):</strong> dpo@multaflow.com</p>
              </div>
            </section>

            {/* 2. Datos que recopilamos */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">2. Datos que Recopilamos</h2>
              </div>
              <div className="pl-13 space-y-4 text-gray-600">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Datos de usuarios:</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Nombre completo</li>
                    <li>Dirección de email</li>
                    <li>Contraseña (cifrada con bcrypt)</li>
                    <li>Fecha de registro y último acceso</li>
                    <li>Dirección IP de conexión</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Datos de multas gestionadas:</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>DNI/NIE del conductor</li>
                    <li>Nombre del conductor</li>
                    <li>Email y teléfono de contacto</li>
                    <li>Dirección postal</li>
                    <li>Matrícula del vehículo</li>
                    <li>Datos de la infracción</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 3. Finalidad */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">3. Finalidad del Tratamiento</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>Sus datos personales serán tratados con las siguientes finalidades:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Gestión de multas de tráfico de su empresa</li>
                  <li>Identificación del conductor responsable</li>
                  <li>Comunicación con organismos sancionadores (DGT, Ayuntamientos)</li>
                  <li>Envío de notificaciones y alertas del sistema</li>
                  <li>Generación de informes y estadísticas</li>
                  <li>Cumplimiento de obligaciones legales</li>
                </ul>
              </div>
            </section>

            {/* 4. Base Legal */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">4. Base Legal</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>El tratamiento de sus datos se fundamenta en:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Consentimiento (Art. 6.1.a RGPD):</strong> Al registrarse acepta esta política</li>
                  <li><strong>Ejecución de contrato (Art. 6.1.b RGPD):</strong> Prestación del servicio contratado</li>
                  <li><strong>Obligación legal (Art. 6.1.c RGPD):</strong> Cumplimiento de normativa de tráfico</li>
                  <li><strong>Interés legítimo (Art. 6.1.f RGPD):</strong> Mejora del servicio y seguridad</li>
                </ul>
              </div>
            </section>

            {/* 5. Conservación */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">5. Conservación de Datos</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Datos de usuario:</strong> Durante la vigencia de la relación + 3 años</li>
                  <li><strong>Datos de multas:</strong> 7 años (obligación fiscal y legal)</li>
                  <li><strong>Logs de acceso:</strong> 1 año</li>
                  <li><strong>Datos de facturación:</strong> 10 años (obligación fiscal)</li>
                </ul>
              </div>
            </section>

            {/* 6. Derechos */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">6. Sus Derechos (RGPD)</h2>
              </div>
              <div className="pl-13 space-y-4 text-gray-600">
                <p>Puede ejercer los siguientes derechos:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Acceso (Art. 15)</h4>
                    <p className="text-sm">Obtener copia de sus datos personales</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Rectificación (Art. 16)</h4>
                    <p className="text-sm">Modificar datos inexactos</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Supresión (Art. 17)</h4>
                    <p className="text-sm">Solicitar eliminación de sus datos</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Portabilidad (Art. 20)</h4>
                    <p className="text-sm">Recibir sus datos en formato estructurado</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Oposición (Art. 21)</h4>
                    <p className="text-sm">Oponerse a determinados tratamientos</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900">Limitación (Art. 18)</h4>
                    <p className="text-sm">Solicitar limitación del tratamiento</p>
                  </div>
                </div>
                <p className="mt-4">
                  Para ejercer sus derechos, contacte con nuestro DPO en{' '}
                  <a href="mailto:dpo@multaflow.com" className="text-blue-600 hover:underline">
                    dpo@multaflow.com
                  </a>
                </p>
              </div>
            </section>

            {/* 7. Seguridad */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">7. Medidas de Seguridad</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Cifrado en tránsito (HTTPS/TLS 1.3)</li>
                  <li>Cifrado en reposo (AES-256)</li>
                  <li>Contraseñas hasheadas con bcrypt</li>
                  <li>Autenticación de dos factores disponible</li>
                  <li>Monitorización y auditoría de accesos</li>
                  <li>Copias de seguridad diarias cifradas</li>
                  <li>Infraestructura en AWS (EU)</li>
                </ul>
              </div>
            </section>

            {/* 8. Cookies */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-pink-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">8. Cookies</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>Este sitio utiliza cookies técnicas necesarias para:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Mantener la sesión de usuario</li>
                  <li>Recordar preferencias</li>
                  <li>Garantizar la seguridad</li>
                </ul>
                <p className="mt-2">No utilizamos cookies de terceros ni de análisis.</p>
              </div>
            </section>

            {/* 9. Reclamaciones */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-gray-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">9. Reclamaciones</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Si considera que el tratamiento de sus datos no es adecuado, puede presentar
                  una reclamación ante la Agencia Española de Protección de Datos (AEPD):
                </p>
                <p className="mt-2">
                  <a
                    href="https://www.aepd.es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    www.aepd.es
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t text-center text-gray-500 text-sm">
            <p>Sistema de Multas - Grupo Ibérica. Todos los derechos reservados</p>
            <p className="mt-2">
              <Link to="/privacidad" className="hover:underline">Privacidad</Link>
              {' · '}
              <Link to="/terminos" className="hover:underline">Términos</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
